import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { lazy, Suspense } from "react";
const ClientTrades = lazy(() => import("@/pages/ClientTrades"));
const Admin = lazy(() => import("@/pages/Admin"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const GetAccess = lazy(() => import("@/pages/GetAccess"));
import axios from "axios";
import {
  TrendingUp, Shield, Zap, BarChart3, Bot, Target,
  ChevronDown, ChevronUp, Send, Menu, X,
  Star, Quote, ArrowRight, CheckCircle2,
  Twitter, MessageCircle, Youtube, Mail, Instagram,
  Clock, Award, Users, Activity, Eye, Cpu,
  Crosshair, Timer, GitBranch, Calendar,
  BarChart2, Lock, Layers, Gauge, Percent,
  ArrowUpRight, ExternalLink, Play, Wallet
} from "lucide-react";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/translations";
import LanguageSelector from "@/components/LanguageSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c55a299e-bc78-43d0-b375-311f1a387269/artifacts/ysqe30t9_Gemini_Generated_Image_5phxjv5phxjv5phx%20MENOR.png";
const BRAND_LOGO_URL = "https://customer-assets.emergentagent.com/job_xauusd-algo-3/artifacts/0j0p7x4z_Gemini_Generated_Image_7pxu457pxu457pxu%20BEST.png";

// ==================== SVG Candlestick Background ====================
const CandlestickBG = () => (
  <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    {[100,200,300,400,500].map(y => (<line key={`h${y}`} x1="0" y1={y} x2="1200" y2={y} stroke="#D4AF37" strokeOpacity="0.08" strokeWidth="0.5" />))}
    {[100,200,300,400,500,600,700,800,900,1000,1100].map(x => (<line key={`v${x}`} x1={x} y1="0" x2={x} y2="600" stroke="#D4AF37" strokeOpacity="0.05" strokeWidth="0.5" />))}
    {[
      { x: 80, o: 380, c: 320, h: 290, l: 410, bull: true },{ x: 130, o: 320, c: 280, h: 250, l: 340, bull: true },
      { x: 180, o: 280, c: 310, h: 260, l: 330, bull: false },{ x: 230, o: 310, c: 260, h: 230, l: 330, bull: true },
      { x: 280, o: 260, c: 290, h: 240, l: 310, bull: false },{ x: 330, o: 290, c: 240, h: 210, l: 310, bull: true },
      { x: 380, o: 240, c: 200, h: 180, l: 260, bull: true },{ x: 430, o: 200, c: 230, h: 180, l: 250, bull: false },
      { x: 480, o: 230, c: 190, h: 170, l: 250, bull: true },{ x: 530, o: 190, c: 220, h: 170, l: 240, bull: false },
      { x: 580, o: 220, c: 180, h: 160, l: 240, bull: true },{ x: 630, o: 180, c: 210, h: 160, l: 230, bull: false },
      { x: 680, o: 210, c: 170, h: 140, l: 230, bull: true },{ x: 730, o: 170, c: 150, h: 120, l: 190, bull: true },
      { x: 780, o: 150, c: 180, h: 130, l: 200, bull: false },{ x: 830, o: 180, c: 140, h: 110, l: 200, bull: true },
      { x: 880, o: 140, c: 170, h: 120, l: 190, bull: false },{ x: 930, o: 170, c: 130, h: 100, l: 190, bull: true },
      { x: 980, o: 130, c: 160, h: 110, l: 180, bull: false },{ x: 1030, o: 160, c: 120, h: 90, l: 180, bull: true },
      { x: 1080, o: 120, c: 100, h: 70, l: 140, bull: true },{ x: 1130, o: 100, c: 130, h: 80, l: 150, bull: false },
    ].map((c, i) => {
      const top = Math.min(c.o, c.c); const bodyH = Math.abs(c.o - c.c);
      const color = c.bull ? "#D4AF37" : "#52525B";
      return (<g key={i}><line x1={c.x} y1={c.h} x2={c.x} y2={c.l} stroke={color} strokeWidth="1.5" strokeOpacity="0.6" /><rect x={c.x - 12} y={top} width="24" height={Math.max(bodyH, 2)} fill={c.bull ? color : "transparent"} stroke={color} strokeWidth="1" opacity="0.6" rx="1" /></g>);
    })}
    <path d="M60,400 Q300,300 500,200 T900,120 L1150,80" fill="none" stroke="#D4AF37" strokeWidth="2" strokeOpacity="0.3" strokeDasharray="4 4" />
  </svg>
);

// ==================== Hooks ====================
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, isVisible];
};

const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={className} style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)', transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s` }}>
      {children}
    </div>
  );
};

// ==================== Particles ====================
const Particles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i, left: Math.random() * 100, size: Math.random() * 3 + 1,
    duration: Math.random() * 15 + 10, delay: Math.random() * 10,
    color: Math.random() > 0.5 ? '#D4AF37' : '#00E5FF'
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map(p => (<div key={p.id} className="particle" style={{ left: `${p.left}%`, bottom: '-10px', width: `${p.size}px`, height: `${p.size}px`, background: p.color, animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s` }} />))}
    </div>
  );
};

// ==================== CountUp ====================
const CountUp = ({ target }) => {
  const [count, setCount] = useState("0");
  const [ref, isVisible] = useInView();
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    const numericTarget = parseFloat(target.replace(/[^0-9.]/g, ''));
    if (isNaN(numericTarget)) { setCount(target); return; }
    const steps = 60;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const eased = numericTarget * (1 - Math.pow(1 - step / steps, 3));
      setCount(Number.isInteger(numericTarget) ? Math.round(eased).toLocaleString() : eased.toFixed(1));
      if (step >= steps) { clearInterval(timer); setCount(target); }
    }, 2000 / steps);
    return () => clearInterval(timer);
  }, [isVisible, target]);
  return <span ref={ref}>{count}</span>;
};

// ==================== HEADER ====================
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [
    { label: t.nav.about, href: "#about" },
    { label: t.nav.performance, href: "#performance" },
    { label: t.nav.onboarding, href: "#how-it-works" },
    { label: t.nav.technology, href: "#technology" },
    { label: t.nav.behindStrategy, href: "#behind-strategy" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];
  return (
    <header data-testid="header" className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? 'nav-glass border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-28 md:h-36">
        <a href="#" className="flex items-center" data-testid="logo-link">
          <img src="/optimus-logo-new.png" alt="Optimus - Automated Gold Trading System" className="h-16 md:h-20 object-contain" />
        </a>
        <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
          {navLinks.map(link => (
            <a key={link.label} href={link.href} data-testid={`nav-${link.href.replace('#','')}`} className="font-sub text-xs font-semibold uppercase tracking-wider text-optimus-white/80 hover:text-optimus-gold transition-colors duration-200">{link.label}</a>
          ))}
          <Link to="/get-access" data-testid="launch-app-btn" className="btn-gold ml-2 px-6 py-2.5 border border-optimus-gold text-optimus-gold font-sub font-bold text-xs uppercase tracking-wider hover:bg-optimus-gold hover:text-black transition-colors duration-300">{t.nav.startOnboarding}</Link>
          <LanguageSelector />
        </nav>
        <div className="lg:hidden flex items-center gap-3">
          <LanguageSelector />
          <button data-testid="mobile-menu-btn" className="text-optimus-white" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="lg:hidden nav-glass border-t border-white/5 px-6 py-6" data-testid="mobile-nav">
          {navLinks.map(link => (<a key={link.label} href={link.href} onClick={() => setMenuOpen(false)} className="block py-3 font-sub text-base font-semibold uppercase tracking-wider text-optimus-white/80 hover:text-optimus-gold transition-colors duration-200">{link.label}</a>))}
          <Link to="/get-access" className="mt-4 block text-center px-6 py-3 border border-optimus-gold text-optimus-gold font-sub font-bold text-sm uppercase tracking-wider">{t.nav.startOnboarding}</Link>
        </div>
      )}
    </header>
  );
};

// ==================== HERO ====================
const HeroSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="hero-bg"><CandlestickBG /><div className="absolute inset-0 grid-pattern z-0" /></div>
      <Particles />
      <div className="scan-line" />
      <div className="glow-orb w-96 h-96 bg-optimus-gold/5 top-1/4 left-1/4" style={{ animationDelay: '0s' }} />
      <div className="glow-orb w-64 h-64 bg-optimus-cyan/5 bottom-1/4 right-1/4" style={{ animationDelay: '3s' }} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-44 md:pt-52 pb-24 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-optimus-gold/30 rounded-full mb-8">
                <div className="w-2 h-2 bg-optimus-cyan rounded-full pulse-ring" />
                <span className="font-sub text-xs uppercase tracking-widest text-optimus-gold font-semibold">{t.hero.badge}</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-optimus-white leading-[1.05] mb-6">
                {t.hero.title}<br /><span className="text-optimus-gold">{t.hero.titleHighlight}</span>
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <p className="font-body text-base md:text-lg leading-relaxed text-optimus-muted max-w-lg mb-4">
                {t.hero.description}
              </p>
              <p className="font-body text-sm text-optimus-cyan/80 italic mb-10">{t.hero.quote}</p>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="flex flex-wrap gap-4">
                <a href="#about" data-testid="hero-learn-btn" className="btn-gold inline-flex items-center gap-2 px-8 py-4 bg-optimus-gold text-black font-sub font-bold text-sm uppercase tracking-wider hover:bg-optimus-gold-hover transition-colors duration-300">
                  {t.hero.learnMore} <ArrowRight size={16} />
                </a>
                <Link to="/get-access" data-testid="hero-launch-btn" className="inline-flex items-center gap-2 px-8 py-4 border border-white/10 text-optimus-white font-sub font-bold text-sm uppercase tracking-wider hover:border-optimus-gold/50 transition-colors duration-300">
                  {t.nav.startOnboarding} <ExternalLink size={14} />
                </Link>
              </div>
            </AnimatedSection>
          </div>
          <AnimatedSection delay={0.2} className="hidden md:flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-optimus-gold/10 blur-[80px] rounded-full" />
              <img src="/optimus-hero-bull.png" alt="Optimus Bull" className="relative w-80 h-80 object-contain animate-float drop-shadow-2xl" data-testid="hero-logo" />
            </div>
          </AnimatedSection>
        </div>

        {/* Stats Section - Full Width */}
        <AnimatedSection delay={0.4}>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { value: t.hero.stats.years, label: t.hero.stats.yearsLabel, icon: Clock, accent: 'gold' },
              { value: t.hero.stats.clients, label: t.hero.stats.clientsLabel, icon: Users, accent: 'cyan' },
              { value: t.hero.stats.capital, label: t.hero.stats.capitalLabel, icon: Wallet, accent: 'cyan' },
              { value: t.hero.stats.global, label: t.hero.stats.globalLabel, icon: Target, accent: 'gold' },
            ].map((stat) => (
              <div key={stat.label} className="relative group">
                <div className={`absolute inset-0 ${stat.accent === 'cyan' ? 'bg-optimus-cyan/5' : 'bg-optimus-gold/5'} rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className={`relative p-5 md:p-6 rounded-lg border ${stat.accent === 'cyan' ? 'border-optimus-cyan/10 hover:border-optimus-cyan/30' : 'border-white/5 hover:border-optimus-gold/20'} bg-white/[0.02] transition-all duration-300 h-full`}>
                  <stat.icon size={18} className={`${stat.accent === 'cyan' ? 'text-optimus-cyan' : 'text-optimus-gold'} mb-3`} />
                  <p className="font-heading text-2xl md:text-3xl font-extrabold text-optimus-white leading-tight">{stat.value}</p>
                  <p className="font-sub text-[10px] md:text-xs uppercase tracking-wider text-optimus-muted mt-1.5">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.5}>
          <div className="mt-6 flex flex-wrap justify-center gap-6">
            {[
              { icon: Eye, text: t.hero.features.transparent },
              { icon: MessageCircle, text: t.hero.features.support },
              { icon: Shield, text: t.hero.features.control },
              { icon: Users, text: t.hero.features.community },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-2">
                <item.icon size={14} className="text-optimus-gold" />
                <span className="font-sub text-xs uppercase tracking-wider text-optimus-muted">{item.text}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-optimus-charcoal to-transparent z-10" />
    </section>
  );
};

// ==================== ABOUT / WHAT IS OPTIMUS ====================
const AboutSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const aboutCards = [
    { icon: Bot, title: t.about.cards.automation.title, desc: t.about.cards.automation.desc },
    { icon: BarChart3, title: t.about.cards.transparency.title, desc: t.about.cards.transparency.desc, comingSoon: true },
    { icon: Users, title: t.about.cards.referral.title, desc: t.about.cards.referral.desc, comingSoon: true },
  ];

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.about.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.about.title} <span className="text-optimus-gold">{t.about.titleHighlight}</span> ?
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4 max-w-3xl mx-auto">
            {t.about.description}
          </p>
        </AnimatedSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {aboutCards.map((f, i) => (
            <AnimatedSection key={f.title} delay={i * 0.1}>
              <div data-testid={`about-card-${i}`} className="glass-card tilt-card p-8 rounded-lg group cursor-default h-full text-center">
                <div className="card-icon w-14 h-14 mx-auto flex items-center justify-center rounded-lg mb-6 bg-optimus-gold/10 text-optimus-gold">
                  <f.icon size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-bold text-optimus-white mb-3 group-hover:text-optimus-gold transition-colors duration-300">{f.title}</h3>
                {f.comingSoon && (
                  <span className="inline-block font-sub text-[10px] uppercase tracking-widest bg-optimus-cyan/10 text-optimus-cyan px-3 py-1 rounded-full font-bold mb-3">{t.about.comingSoon}</span>
                )}
                <p className="font-body text-sm leading-relaxed text-optimus-muted whitespace-pre-line">{f.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
        <AnimatedSection className="text-center mt-10">
          <p className="font-body text-sm text-optimus-muted">
            {t.about.faqLink} <a href="#faq" className="text-optimus-cyan hover:text-optimus-gold transition-colors duration-200 underline underline-offset-2">{t.about.faqLinkText}</a>.
          </p>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== PERFORMANCE ====================
const DEFAULT_MONTHLY_2025 = [
  { month: "Jan", value: 5.15 },{ month: "Feb", value: 6.68 },{ month: "Mar", value: 11.52 },
  { month: "Apr", value: 5.90 },{ month: "May", value: 10.40 },{ month: "Jun", value: 5.27 },
  { month: "Jul", value: 8.85 },{ month: "Aug", value: 6.83 },{ month: "Sep", value: 10.57 },
  { month: "Oct", value: 12.08 },{ month: "Nov", value: 6.57 },{ month: "Dec", value: 4.78 },
];

const YEARS = ["2025", "2026"];

const PerformanceSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  const [selectedYear, setSelectedYear] = useState("2026");
  const [perfData, setPerfData] = useState({
    cumulative_gain: "0.00", avg_monthly: "0.00", max_drawdown: "0.00",
    win_rate: "0", this_week: "0.00", this_month: "0.00", this_year: "0.00",
    monthly_data: [], last_updated: ""
  });
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    fetch(`${API}/performance?year=2026`).then(r => r.json()).then(data => {
      if (data && data.cumulative_gain) setPerfData(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/performance?year=${selectedYear}`).then(r => r.json()).then(data => {
      if (data?.monthly_data?.length > 0) setMonthlyData(data.monthly_data);
      else if (selectedYear === "2025") setMonthlyData(DEFAULT_MONTHLY_2025);
      else setMonthlyData([]);
    }).catch(() => {});
  }, [selectedYear]);

  return (
    <section id="performance" data-testid="performance-section" className="relative py-24 md:py-32 overflow-hidden">
      <Particles />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.performance.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.performance.title} <span className="text-optimus-gold">{t.performance.titleHighlight}</span>
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4 max-w-2xl mx-auto">
            {t.performance.description}
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <AnimatedSection>
            <div className="glass-card rounded-lg p-8 md:p-10 h-full">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full pulse-ring" />
                <span className="font-sub text-xs uppercase tracking-widest text-emerald-400 font-semibold">{t.performance.liveData}</span>
              </div>
              <div className="space-y-6">
                <div className="text-center">
                  <p className="font-sub text-xs uppercase tracking-widest text-optimus-dim mb-2">{t.performance.cumulative}</p>
                  <p data-testid="perf-cumulative" className="stat-value font-heading text-5xl md:text-6xl font-extrabold">
                    +<CountUp target={perfData.cumulative_gain} />%
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="font-sub text-xs uppercase tracking-widest text-optimus-dim mb-1">{t.performance.avgMonthly}</p>
                    <p data-testid="perf-monthly" className="stat-value font-heading text-2xl md:text-3xl font-extrabold"><CountUp target={perfData.avg_monthly} />%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-sub text-xs uppercase tracking-widest text-optimus-dim mb-1">{t.performance.maxDrawdown}</p>
                    <p data-testid="perf-drawdown" className="font-heading text-2xl md:text-3xl font-extrabold text-optimus-white"><CountUp target={perfData.max_drawdown} />%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-sub text-xs uppercase tracking-widest text-optimus-dim mb-1">{t.performance.winRate}</p>
                    <p data-testid="perf-winrate" className="font-heading text-2xl md:text-3xl font-extrabold text-emerald-400"><CountUp target={perfData.win_rate} />%</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/5">
                  <div className="text-center">
                    <p className="font-sub text-[10px] uppercase tracking-widest text-optimus-dim mb-1">{t.performance.thisWeek}</p>
                    <p data-testid="perf-this-week" className="font-heading text-lg md:text-xl font-bold text-optimus-cyan">
                      {parseFloat(perfData.this_week) >= 0 ? "+" : ""}<CountUp target={perfData.this_week} />%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="font-sub text-[10px] uppercase tracking-widest text-optimus-dim mb-1">{t.performance.thisMonth}</p>
                    <p data-testid="perf-this-month" className="font-heading text-lg md:text-xl font-bold text-optimus-cyan">
                      {parseFloat(perfData.this_month) >= 0 ? "+" : ""}<CountUp target={perfData.this_month} />%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="font-sub text-[10px] uppercase tracking-widest text-optimus-dim mb-1">{t.performance.thisYear}</p>
                    <p data-testid="perf-this-year" className="font-heading text-lg md:text-xl font-bold text-optimus-cyan">
                      {parseFloat(perfData.this_year) >= 0 ? "+" : ""}<CountUp target={perfData.this_year} />%
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                  <Shield size={16} className="text-optimus-cyan" />
                  <span className="font-body text-sm text-optimus-muted">{t.performance.tracked} &bull; {t.performance.liveFeed}</span>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <div className="glass-card rounded-lg p-5 md:p-8 lg:p-10 h-full flex flex-col">
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-heading text-lg md:text-xl font-bold text-optimus-white">{t.performance.chartTitle} {selectedYear}</h3>
                <div className="flex items-center gap-0" data-testid="year-toggle">
                  {YEARS.map(year => (
                    <button
                      key={year}
                      data-testid={`year-btn-${year}`}
                      onClick={() => setSelectedYear(year)}
                      className={`px-3 py-1 font-sub text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                        selectedYear === year
                          ? "bg-optimus-gold text-black"
                          : "bg-white/5 text-optimus-muted hover:bg-white/10 hover:text-optimus-white"
                      } ${year === YEARS[0] ? "rounded-l" : "rounded-r"}`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
              <p className="font-body text-xs text-optimus-dim mb-6 md:mb-8">{t.performance.chartDisclaimer}</p>
              {monthlyData.length === 0 ? (
                <div className="flex-1 flex items-center justify-center min-h-[220px] md:min-h-[280px]">
                  <p className="font-body text-sm text-optimus-dim">{language === "pt" ? "Dados mensais serão adicionados em breve" : "Monthly data will be added soon"}</p>
                </div>
              ) : (
              <div className="flex-1 flex min-h-[220px] md:min-h-[280px]">
                <div className="flex flex-col justify-between pr-1.5 md:pr-3 pb-5" style={{ height: '180px' }}>
                  {["15%", "10%", "5%", "0%"].map(label => (
                    <span key={label} className="font-sub text-[8px] md:text-[10px] text-optimus-dim text-right w-6 md:w-8">{label}</span>
                  ))}
                </div>
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="relative flex-1" style={{ height: '180px' }}>
                    {[0, 33.33, 66.66, 100].map((pct, i) => (
                      <div key={i} className="absolute left-0 right-0" style={{ top: `${pct}%`, height: '1px', background: 'rgba(255,255,255,0.05)' }} />
                    ))}
                    <div className="absolute inset-0 flex items-end gap-[3px] md:gap-1.5">
                      {monthlyData.map((d) => {
                        const maxVal = 15;
                        const heightPct = Math.min((d.value / maxVal) * 100, 100);
                        const isHigh = d.value >= 10;
                        return (
                          <div key={d.month} className="flex flex-col items-center flex-1 h-full justify-end min-w-0">
                            <span className="font-sub text-[7px] md:text-[9px] font-bold text-optimus-white mb-0.5 md:mb-1 whitespace-nowrap">{d.value}%</span>
                            <div className="w-full rounded-t-sm" style={{
                              height: `${heightPct}%`,
                              background: isHigh ? 'linear-gradient(to top, rgba(212,175,55,0.5), rgba(212,175,55,0.85))' : 'linear-gradient(to top, rgba(0,229,255,0.25), rgba(0,229,255,0.55))',
                              boxShadow: isHigh ? '0 0 12px rgba(212,175,55,0.2)' : '0 0 8px rgba(0,229,255,0.1)',
                            }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-[3px] md:gap-1.5 mt-1.5">
                    {monthlyData.map((d, i) => (
                      <span key={i} className="flex-1 text-center font-sub text-[7px] md:text-[9px] uppercase tracking-wider text-optimus-dim">{d.month.substring(0,3)}</span>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection className="text-center mt-10">
          <Link to="/client-trades" data-testid="performance-clients-trades-btn" className="inline-flex items-center gap-2 px-10 py-4 border border-optimus-cyan/40 text-optimus-cyan font-sub font-bold text-sm uppercase tracking-wider hover:bg-optimus-cyan/10 hover:border-optimus-cyan transition-all duration-300">
            {t.performance.clientsTrades} <Eye size={16} />
          </Link>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== HOW IT WORKS ====================
const HowItWorksSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const steps = [
    { num: "01", title: t.howItWorks.steps.step1.title, desc: t.howItWorks.steps.step1.desc, tag: null },
    { num: "02", title: t.howItWorks.steps.step2.title, desc: t.howItWorks.steps.step2.desc, tag: null },
    { num: "03", title: t.howItWorks.steps.step3.title, desc: t.howItWorks.steps.step3.desc, tag: null },
    { num: "04", title: t.howItWorks.steps.step4.title, desc: t.howItWorks.steps.step4.desc, tag: null },
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.howItWorks.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.howItWorks.title} <span className="text-optimus-gold">{t.howItWorks.titleHighlight}</span> {t.howItWorks.titleEnd}
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4">{t.howItWorks.description}</p>
        </AnimatedSection>
        <div className="space-y-6">
          {steps.map((s, i) => (
            <AnimatedSection key={s.num} delay={i * 0.1}>
              <div data-testid={`step-card-${i}`} className="glass-card tilt-card rounded-lg p-6 md:p-8 flex gap-6 items-start">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-lg bg-optimus-gold/10">
                  <span className="font-heading text-xl font-extrabold text-optimus-gold">{s.num}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading text-lg font-bold text-optimus-white">{s.title}</h3>
                    {s.tag && (
                      <span className="font-sub text-[10px] uppercase tracking-widest bg-optimus-cyan/10 text-optimus-cyan px-3 py-1 rounded-full font-bold">{s.tag}</span>
                    )}
                  </div>
                  <p className="font-body text-sm leading-relaxed text-optimus-muted mt-2 whitespace-pre-line">{s.desc}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
        <AnimatedSection className="mt-8">
          <div className="text-center">
            <span className="font-sub text-[10px] uppercase tracking-widest bg-optimus-cyan/10 text-optimus-cyan px-4 py-1.5 rounded-full font-bold">{t.howItWorks.supportIncluded}</span>
          </div>
          <p className="font-body text-sm text-optimus-muted mt-6 leading-relaxed whitespace-pre-line text-center">
            {t.howItWorks.supportText}
          </p>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== TECHNOLOGY ====================
const TechnologySection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const techFeatures = [
    { icon: Target, title: t.technology.features.xauusd.title, desc: t.technology.features.xauusd.desc },
    { icon: Crosshair, title: t.technology.features.scalping.title, desc: t.technology.features.scalping.desc },
    { icon: Timer, title: t.technology.features.exposure.title, desc: t.technology.features.exposure.desc },
    { icon: Cpu, title: t.technology.features.ai.title, desc: t.technology.features.ai.desc },
    { icon: Layers, title: t.technology.features.indicators.title, desc: t.technology.features.indicators.desc },
    { icon: Shield, title: t.technology.features.protection.title, desc: t.technology.features.protection.desc },
    { icon: Lock, title: t.technology.features.drawdown.title, desc: t.technology.features.drawdown.desc },
    { icon: GitBranch, title: t.technology.features.hedging.title, desc: t.technology.features.hedging.desc },
    { icon: BarChart2, title: t.technology.features.frequency.title, desc: t.technology.features.frequency.desc },
  ];

  return (
    <section id="technology" data-testid="technology-section" className="relative py-24 md:py-32 overflow-hidden">
      <Particles />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.technology.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.technology.title} <span className="text-optimus-gold">{t.technology.titleHighlight}</span>
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4 max-w-2xl mx-auto">
            {t.technology.description}
          </p>
        </AnimatedSection>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {techFeatures.map((f, i) => (
            <AnimatedSection key={f.title} delay={i * 0.06}>
              <div data-testid={`tech-card-${i}`} className="glass-card tilt-card p-6 rounded-lg group cursor-default h-full">
                <div className={`card-icon w-10 h-10 flex items-center justify-center rounded-lg mb-4 ${i % 2 === 0 ? 'bg-optimus-gold/10 text-optimus-gold' : 'bg-optimus-cyan/10 text-optimus-cyan'}`}>
                  <f.icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-bold text-optimus-white mb-2 group-hover:text-optimus-gold transition-colors duration-300">{f.title}</h3>
                <p className="font-body text-sm leading-relaxed text-optimus-muted">{f.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== BEHIND THE STRATEGY ====================
const BehindStrategySection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  const [winRate, setWinRate] = useState("77");

  useEffect(() => {
    fetch(`${API}/performance`).then(r => r.json()).then(data => {
      if (data?.win_rate) setWinRate(data.win_rate);
    }).catch(() => {});
  }, []);

  return (
    <section id="behind-strategy" data-testid="behind-strategy-section" className="relative py-24 md:py-32 overflow-hidden">
      <Particles />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero headline */}
        <AnimatedSection className="text-center mb-20">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.behindStrategy.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.behindStrategy.title} <span className="text-optimus-gold">{t.behindStrategy.titleHighlight.replace("{rate}", winRate)}</span>
          </h2>
          <p className="font-body text-base md:text-lg text-optimus-muted mt-4 max-w-3xl mx-auto">
            {t.behindStrategy.description}
          </p>
        </AnimatedSection>

        {/* Two-column layout: Philosophy + Risk */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Selective Trading */}
          <AnimatedSection>
            <div className="glass-card tilt-card rounded-lg p-8 h-full">
              <div className="card-icon w-12 h-12 flex items-center justify-center rounded-lg mb-5 bg-optimus-gold/10 text-optimus-gold">
                <Target size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold text-optimus-white mb-3">{t.behindStrategy.selectiveTrading.title}</h3>
              <p className="font-body text-sm leading-relaxed text-optimus-muted mb-4">
                {t.behindStrategy.selectiveTrading.desc1}
              </p>
              <p className="font-body text-sm leading-relaxed text-optimus-muted">
                {t.behindStrategy.selectiveTrading.desc2}
              </p>
            </div>
          </AnimatedSection>

          {/* Built-in Risk Control */}
          <AnimatedSection delay={0.1}>
            <div className="glass-card tilt-card rounded-lg p-8 h-full">
              <div className="card-icon w-12 h-12 flex items-center justify-center rounded-lg mb-5 bg-optimus-cyan/10 text-optimus-cyan">
                <Shield size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold text-optimus-white mb-3">{t.behindStrategy.riskControl.title}</h3>
              <div className="space-y-3 mt-4">
                {t.behindStrategy.riskControl.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 size={16} className="text-optimus-gold flex-shrink-0" />
                    <span className="font-body text-sm text-optimus-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Full-width: Transparency */}
        <AnimatedSection className="mb-8">
          <div className="glass-card tilt-card rounded-lg p-8 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-optimus-gold/5 rounded-full blur-[80px]" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="card-icon w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-lg bg-optimus-gold/10 text-optimus-gold">
                <Eye size={28} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-optimus-white mb-2">{t.behindStrategy.transparency.title}</h3>
                <p className="font-body text-sm leading-relaxed text-optimus-muted">
                  {t.behindStrategy.transparency.desc}
                </p>
                <p className="font-heading text-sm font-bold text-optimus-gold mt-3 uppercase tracking-wider">
                  {t.behindStrategy.transparency.highlight}
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Two-column: Balance + No Intervention */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Balance Fluctuation */}
          <AnimatedSection>
            <div className="glass-card tilt-card rounded-lg p-8 h-full">
              <div className="card-icon w-12 h-12 flex items-center justify-center rounded-lg mb-5 bg-optimus-gold/10 text-optimus-gold">
                <Activity size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold text-optimus-white mb-3">{t.behindStrategy.fluctuation.title}</h3>
              <p className="font-body text-sm leading-relaxed text-optimus-muted mb-3">
                {t.behindStrategy.fluctuation.desc}
              </p>
              <p className="font-body text-sm leading-relaxed text-optimus-cyan/80 italic">
                {t.behindStrategy.fluctuation.italic}
              </p>
            </div>
          </AnimatedSection>

          {/* No Intervention */}
          <AnimatedSection delay={0.1}>
            <div className="glass-card tilt-card rounded-lg p-8 h-full">
              <div className="card-icon w-12 h-12 flex items-center justify-center rounded-lg mb-5 bg-optimus-cyan/10 text-optimus-cyan">
                <Cpu size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold text-optimus-white mb-3">{t.behindStrategy.noIntervention.title}</h3>
              <p className="font-body text-sm leading-relaxed text-optimus-muted">
                {t.behindStrategy.noIntervention.desc}
              </p>
            </div>
          </AnimatedSection>
        </div>

        {/* When OPTIMUS Doesn't Trade - 3 cards */}
        <AnimatedSection className="mb-4">
          <h3 className="font-heading text-lg md:text-2xl font-bold text-optimus-white mb-6">
            {t.behindStrategy.whenNotTrade.title} <span className="text-optimus-gold">{t.behindStrategy.whenNotTrade.titleHighlight}</span>
          </h3>
        </AnimatedSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: BarChart2, title: t.behindStrategy.whenNotTrade.marketConditions.title, desc: t.behindStrategy.whenNotTrade.marketConditions.desc },
            { icon: GitBranch, title: t.behindStrategy.whenNotTrade.gridSetup.title, desc: t.behindStrategy.whenNotTrade.gridSetup.desc },
            { icon: Calendar, title: t.behindStrategy.whenNotTrade.events.title, desc: t.behindStrategy.whenNotTrade.events.desc },
          ].map((item, i) => (
            <AnimatedSection key={item.title} delay={i * 0.1}>
              <div className="glass-card tilt-card rounded-lg p-6 h-full group">
                <div className={`card-icon w-10 h-10 flex items-center justify-center rounded-lg mb-4 ${i % 2 === 0 ? 'bg-optimus-gold/10 text-optimus-gold' : 'bg-optimus-cyan/10 text-optimus-cyan'}`}>
                  <item.icon size={20} strokeWidth={1.5} />
                </div>
                <h4 className="font-heading text-base font-bold text-optimus-white mb-2 group-hover:text-optimus-gold transition-colors duration-300">{item.title}</h4>
                <p className="font-body text-sm leading-relaxed text-optimus-muted">{item.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* CTA */}
        <AnimatedSection className="text-center">
          <div className="glass-card rounded-lg p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 bg-optimus-gold/3 rounded-lg" />
            <div className="relative">
              <h3 className="font-heading text-2xl md:text-4xl font-extrabold text-optimus-white mb-4">
                {t.behindStrategy.cta.title} <span className="text-optimus-gold">{t.behindStrategy.cta.titleHighlight}</span>
              </h3>
              <p className="font-body text-base text-optimus-muted mb-6">
                {t.behindStrategy.cta.desc}
              </p>
              
              {/* Exclusive Marketing Message */}
              <div className="inline-flex items-center gap-3 px-6 py-3 mb-8 rounded-full border border-optimus-cyan/30 bg-optimus-cyan/5">
                <Award size={18} className="text-optimus-cyan flex-shrink-0" />
                <p className="font-body text-sm text-optimus-white">
                  {t.behindStrategy.cta.exclusive} <span className="text-optimus-cyan font-semibold">{t.behindStrategy.cta.exclusiveHighlight}</span>
                </p>
              </div>
              
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                <Link to="/client-trades" data-testid="clients-trades-btn" className="inline-flex items-center gap-2 px-10 py-4 border border-optimus-cyan/40 text-optimus-cyan font-sub font-bold text-sm uppercase tracking-wider hover:bg-optimus-cyan/10 hover:border-optimus-cyan transition-all duration-300">
                  {t.performance.clientsTrades} <Eye size={16} />
                </Link>
                <Link to="/get-access" className="btn-gold inline-flex items-center gap-2 px-10 py-4 bg-optimus-gold text-black font-sub font-bold text-sm uppercase tracking-wider hover:bg-optimus-gold-hover transition-colors duration-300">
                  {t.nav.startOnboarding} <ArrowRight size={16} />
                </Link>
              </div>
              <p className="font-body text-xs text-optimus-dim mt-6">
                {t.behindStrategy.cta.faqNote} <a href="#faq" className="text-optimus-cyan hover:text-optimus-gold transition-colors duration-200 underline underline-offset-2">{t.behindStrategy.cta.faqLink}</a>.
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== PRICING ====================
const PricingSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.pricing.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.pricing.title} <span className="text-optimus-gold">{t.pricing.titleHighlight}</span>
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4 whitespace-pre-line">
            {t.pricing.description}
          </p>
        </AnimatedSection>

        <AnimatedSection>
          <div className="glass-card pricing-highlight rounded-lg p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-optimus-gold/5 rounded-full blur-[100px]" />
            <div className="relative grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
              {/* Left - 30% showcase */}
              <div className="md:col-span-2 text-center flex flex-col items-center">
                <span className="font-sub text-xs uppercase tracking-widest bg-optimus-gold text-black px-3 py-1 font-bold">{t.pricing.performanceFee}</span>
                <div className="mt-6 mb-4">
                  <p className="stat-value font-heading text-7xl md:text-8xl font-extrabold leading-none">30%</p>
                </div>
                <p className="font-sub text-sm uppercase tracking-wider text-optimus-cyan-dim">{t.pricing.serviceFee}</p>
                <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5">
                  <p className="font-body text-xs text-optimus-gold/70 font-semibold flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-optimus-gold/60 shrink-0" />{t.pricing.minimumInvestment}</p>
                  <p className="font-body text-xs text-optimus-gold/70 font-semibold flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-optimus-gold/60 shrink-0" />{t.pricing.additionalCost}</p>
                </div>
                <div className="mt-8">
                  <Link to="/get-access" className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 bg-optimus-gold text-black font-sub font-bold text-base md:text-lg uppercase tracking-wider hover:bg-optimus-gold-hover transition-colors duration-300">
                    {t.nav.startOnboarding} <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex md:col-span-1 justify-center">
                <div className="w-px h-48 bg-gradient-to-b from-transparent via-optimus-cyan/30 to-transparent" />
              </div>
              <div className="md:hidden w-full h-px bg-gradient-to-r from-transparent via-optimus-cyan/30 to-transparent" />

              {/* Right - Benefits */}
              <div className="md:col-span-2 flex flex-col justify-center space-y-8">
                {[
                  { icon: Percent, title: t.pricing.benefits.fee.title, desc: t.pricing.benefits.fee.desc },
                  { icon: TrendingUp, title: t.pricing.benefits.resultsBased.title, desc: t.pricing.benefits.resultsBased.desc },
                  { icon: Users, title: t.pricing.benefits.community.title, desc: t.pricing.benefits.community.desc },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start group">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-optimus-cyan/10 text-optimus-cyan group-hover:bg-optimus-cyan/20 transition-colors duration-200">
                      <item.icon size={18} />
                    </div>
                    <div>
                      <p className="font-heading text-sm font-bold text-optimus-white">{item.title}</p>
                      <p className="font-body text-sm text-optimus-muted mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== TESTIMONIALS ====================
const TestimonialsSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  return (
    <section data-testid="testimonials-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.testimonials.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.testimonials.title} <span className="text-optimus-gold">{t.testimonials.titleHighlight}</span>
          </h2>
        </AnimatedSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.testimonials.items.map((testimonial, i) => (
            <AnimatedSection key={testimonial.name} delay={i * 0.1}>
              <div data-testid={`testimonial-card-${i}`} className="testimonial-card tilt-card rounded-lg p-8 h-full flex flex-col">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (<Star key={j} size={14} fill="#D4AF37" stroke="#D4AF37" />))}
                </div>
                <Quote size={24} className="text-optimus-gold/30 mb-4" />
                <p className="font-body text-sm leading-relaxed text-optimus-muted flex-1 mb-6">"{testimonial.text}"</p>
                <div>
                  <p className="font-heading text-sm font-bold text-optimus-white">{testimonial.name}</p>
                  <p className="font-sub text-xs text-optimus-dim uppercase tracking-wider">{testimonial.role}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== FAQ ====================
const FAQItem = ({ question, answer, id, badge }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5" data-testid={`faq-item-${id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group" data-testid={`faq-trigger-${id}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`font-heading text-sm md:text-base font-bold transition-colors duration-200 ${open ? 'text-optimus-gold' : 'text-optimus-white group-hover:text-optimus-gold'}`}>{question}</span>
          {badge && (
            <span className="font-sub text-[10px] uppercase tracking-widest bg-optimus-cyan/10 text-optimus-cyan px-2 py-0.5 rounded-full font-bold">{badge}</span>
          )}
        </div>
        <ChevronDown size={16} className={`text-optimus-muted flex-shrink-0 ml-4 transition-transform duration-300 ${open ? 'rotate-180 text-optimus-gold' : ''}`} />
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? '500px' : '0', opacity: open ? 1 : 0 }}>
        <p className="pb-5 font-body text-sm leading-relaxed text-optimus-muted">{answer}</p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const faqCategories = [
    { title: t.faq.categories.general.title, items: t.faq.categories.general.items },
    { title: t.faq.categories.security.title, items: t.faq.categories.security.items },
    { title: t.faq.categories.strategy.title, items: t.faq.categories.strategy.items },
    { title: t.faq.categories.risk.title, items: t.faq.categories.risk.items },
    { title: t.faq.categories.setup.title, items: t.faq.categories.setup.items },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.faq.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.faq.title} <span className="text-optimus-gold">{t.faq.titleHighlight}</span>
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4">{t.faq.description}</p>
        </AnimatedSection>

        <AnimatedSection>
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12" data-testid="faq-category-tabs">
            {faqCategories.map((cat, i) => (
              <button
                key={i}
                onClick={() => setActiveCategory(i)}
                data-testid={`faq-tab-${i}`}
                className={`px-4 py-2 rounded-full font-sub text-xs uppercase tracking-wider font-bold transition-all duration-300 border ${
                  activeCategory === i
                    ? 'bg-optimus-gold text-black border-optimus-gold'
                    : 'bg-transparent text-optimus-muted border-white/10 hover:border-optimus-gold/50 hover:text-optimus-white'
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>

          {/* Questions for active category */}
          <div className="glass-card rounded-lg p-6 md:p-10" data-testid="faq-content">
            <h3 className="font-heading text-lg md:text-xl font-bold text-optimus-cyan mb-6">{faqCategories[activeCategory].title}</h3>
            {faqCategories[activeCategory].items.map((item, i) => (
              <FAQItem key={`${activeCategory}-${i}`} question={item.q} answer={item.a} id={`${activeCategory}-${i}`} badge={item.badge} />
            ))}
          </div>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== CONTACT ====================
const ContactSection = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: 'sending', message: '' });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setStatus({ type: 'success', message: t.contact.successMessage });
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus({ type: '', message: '' }), 5000);
      } else {
        setStatus({ type: 'error', message: t.contact.errorMessage });
      }
    } catch {
      setStatus({ type: 'error', message: t.contact.errorMessage });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 overflow-hidden">
      <Particles />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="font-sub text-sm uppercase tracking-widest text-optimus-cyan font-semibold">{t.contact.sectionTitle}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-optimus-white mt-4">
            {t.contact.title} <span className="text-optimus-gold">{t.contact.titleHighlight}</span>
          </h2>
          <p className="font-body text-base text-optimus-muted mt-4 max-w-2xl mx-auto">
            {t.contact.description}
          </p>
        </AnimatedSection>

        <AnimatedSection>
          <div className="glass-card rounded-lg p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-optimus-gold/5 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-optimus-cyan/5 rounded-full blur-[80px]" />
            
            <form onSubmit={handleSubmit} className="relative space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-sub text-xs uppercase tracking-widest text-optimus-muted mb-2">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-optimus-white font-body text-sm placeholder-optimus-dim focus:outline-none focus:border-optimus-gold/50 focus:ring-1 focus:ring-optimus-gold/20 transition-all duration-200"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block font-sub text-xs uppercase tracking-widest text-optimus-muted mb-2">
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-optimus-white font-body text-sm placeholder-optimus-dim focus:outline-none focus:border-optimus-gold/50 focus:ring-1 focus:ring-optimus-gold/20 transition-all duration-200"
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block font-sub text-xs uppercase tracking-widest text-optimus-muted mb-2">
                  {t.contact.form.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-message"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-optimus-white font-body text-sm placeholder-optimus-dim focus:outline-none focus:border-optimus-gold/50 focus:ring-1 focus:ring-optimus-gold/20 transition-all duration-200 resize-none"
                  placeholder={t.contact.form.messagePlaceholder}
                />
              </div>

              {status.message && (
                <div className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
                  <p className="font-body text-sm">{status.message}</p>
                </div>
              )}

              <div className="text-center pt-4">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.type === 'sending'}
                  className="btn-gold inline-flex items-center gap-2 px-10 py-4 bg-optimus-gold text-black font-sub font-bold text-sm uppercase tracking-wider hover:bg-optimus-gold-hover transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status.type === 'sending' ? (language === 'pt' ? 'A enviar...' : 'Sending...') : t.contact.form.submit} <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </AnimatedSection>
      </div>
      <div className="section-divider mt-24 md:mt-32" />
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const navLinks = [
    { label: t.nav.about, href: "#about" },
    { label: t.nav.performance, href: "#performance" },
    { label: t.nav.onboarding, href: "#how-it-works" },
    { label: t.nav.technology, href: "#technology" },
    { label: t.nav.behindStrategy, href: "#behind-strategy" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const handleSocialClick = (e) => {
    e.preventDefault();
    // Social links disabled for now
  };
  
  return (
    <footer data-testid="footer" className="relative border-t border-white/5 py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src="/optimus-logo-new.png" alt="Optimus" className="h-10 md:h-12 object-contain" />
            </div>
            <p className="font-body text-sm text-optimus-muted max-w-sm leading-relaxed mb-6">
              {t.footer.description}
            </p>
            <div className="flex flex-col gap-4">
              {/* Instagram with community label */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.instagram.com/confrariadotrader_oficial"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-instagram" 
                  className="social-icon w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-optimus-muted hover:text-optimus-gold hover:border-optimus-gold/40 transition-colors duration-200" 
                  aria-label="Instagram"
                >
                  <Instagram size={16} />
                </a>
                <span className="font-sub text-xs uppercase tracking-wider text-optimus-muted">
                  {t.footer.instagramCommunity}
                </span>
              </div>
              {/* Other social buttons */}
              <div className="flex items-center gap-3">
                <a 
                  href="#contact"
                  data-testid="social-email" 
                  className="social-icon w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-optimus-muted hover:text-optimus-gold hover:border-optimus-gold/40 transition-colors duration-200" 
                  aria-label="Email"
                >
                  <Mail size={16} />
                </a>
                <span className="font-sub text-xs uppercase tracking-wider text-optimus-muted">Email</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-sub text-sm font-bold uppercase tracking-widest text-optimus-gold mb-6">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {navLinks.map(link => (
                <li key={link.href}><a href={link.href} className="font-body text-sm text-optimus-muted hover:text-optimus-white transition-colors duration-200">{link.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-sub text-sm font-bold uppercase tracking-widest text-optimus-gold mb-6">{t.footer.legal}</h4>
            <ul className="space-y-3">
              {[
                { label: t.footer.legalLinks.privacy, path: "/legal/privacy" },
                { label: t.footer.legalLinks.terms, path: "/legal/terms" },
                { label: t.footer.legalLinks.risk, path: "/legal/risk" },
                { label: t.footer.legalLinks.cookie, path: "/legal/cookies" },
              ].map(link => (
                <li key={link.path}><Link to={link.path} className="font-body text-sm text-optimus-muted hover:text-optimus-white transition-colors duration-200">{link.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="section-divider mt-12 mb-8" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-optimus-dim">&copy; {new Date().getFullYear()} {t.footer.copyright}</p>
          <p className="font-body text-[10px] text-optimus-dim/60 max-w-lg text-center md:text-right">
            {t.footer.riskWarning}
          </p>
        </div>
      </div>
    </footer>
  );
};

// ==================== SCROLL TO TOP ====================
const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <button
      data-testid="scroll-to-top-btn"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-8 right-8 z-50 w-12 h-12 flex items-center justify-center rounded-full border border-optimus-gold/40 bg-optimus-charcoal/80 backdrop-blur-md text-optimus-gold hover:bg-optimus-gold hover:text-black transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      aria-label="Scroll to top"
    >
      <ChevronUp size={20} />
    </button>
  );
};

// ==================== MAIN PAGE ====================
const LandingPage = () => (
  <div className="relative bg-optimus-charcoal min-h-screen overflow-hidden">
    <div className="noise-overlay" />
    <Header />
    <main>
      <HeroSection />
      <AboutSection />
      <PerformanceSection />
      <HowItWorksSection />
      <TechnologySection />
      <BehindStrategySection />
      <PricingSection />
      {/* <TestimonialsSection /> */}
      <FAQSection />
      <ContactSection />
    </main>
    <Footer />
    <ScrollToTop />
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/client-trades" element={<Suspense fallback={<div className="bg-optimus-charcoal min-h-screen" />}><ClientTrades /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={<div className="bg-optimus-charcoal min-h-screen" />}><Admin /></Suspense>} />
          <Route path="/legal/:page" element={<Suspense fallback={<div className="bg-optimus-charcoal min-h-screen" />}><LegalPage /></Suspense>} />
          <Route path="/get-access" element={<Suspense fallback={<div className="bg-optimus-charcoal min-h-screen" />}><GetAccess /></Suspense>} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
